/**
 * API Service - Centralized single point of truth for all API requests
 *
 * This service provides:
 * - HTTP client with integrated Auth0 authentication
 * - Automatic CSRF token handling for write operations
 * - Centralized endpoint definitions
 * - Consistent error handling
 * - Strong typing for all responses
 *
 * @module @shared/services/apiService
 */

import type {
    OrgEmployee,
    Employee,
    RestList,
    EmployeeMessage,
} from '@shared/types';
import type {
    EmployeeRequest,
    OrgEmployeeRequest,
    PutAvailabilityPattern,
    EmployeeRequestsList,
    OrgEmployeeRequestsList,
    PutCommentRequest,
    Org,
} from '@shared/models/requestTypes';

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api/v1`;
const CSRF_URL = `${import.meta.env.VITE_API_URL}/csrf`;

// ============================================================================
// TYPES
// ============================================================================

/**
 * Supported HTTP methods
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Request configuration
 */
interface RequestConfig<T = unknown> {
    method: HttpMethod;
    url: string;
    body?: T;
    requiresCsrf?: boolean;
}

/**
 * Typed API error
 */
export interface ApiError extends Error {
    status: number;
    statusText: string;
    errorCode?: string;
    description?: string;
    name: string;
}

/**
 * API result type
 */
export type ApiResult<T> = Promise<T>;

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Error code to user-friendly message mapping
 */
const ERROR_MESSAGES: Record<string, string> = {
    INVALID_CHARACTERS:
        'You have entered invalid characters. Please use only letters, numbers, and special characters like .,?!()',
    ILLEGAL_ARGUMENT: 'Invalid data',
    ORG_EMPLOYEE_NOT_FOUND:
        'We could not find your employee in the Dynamon system.',
    MODULE_NOT_ENABLED: 'Your organization does not support employee login',
    ORG_EMPLOYEE_ACCESS_FORBIDDEN: 'You do not have access to this employee.',
    EMPLOYEE_NOT_FOUND: 'Employee not found.',
    EMPLOYEE_ACCESS_FORBIDDEN: 'You do not have access to this employee.',
    ORG_NOT_FOUND: 'Organization not found.',
    REQUEST_NOT_FOUND: 'Request not found.',
    REQUEST_ACCESS_FORBIDDEN: 'You do not have access to this request.',
    REQUEST_DEADLINE_EXPIRED: 'The deadline for this request has expired.',
    REQUEST_LOCKED: 'The request is locked and cannot be modified.',
    UNAUTHORIZED: 'Not authenticated. Please log in again.',
    BAD_REQUEST: 'Invalid request.',
    GENERAL_ERROR: 'Something went wrong. Please try again later.',
};

/**
 * Gets a user-friendly error message based on the error code
 */
function getErrorMessage(errorCode: string | undefined): string {
    if (errorCode && errorCode in ERROR_MESSAGES) {
        return ERROR_MESSAGES[errorCode];
    }
    return ERROR_MESSAGES.GENERAL_ERROR;
}

/**
 * Creates a typed API error
 */
function createApiError(
    message: string,
    status: number,
    statusText: string,
    errorCode?: string,
    description?: string,
): ApiError {
    const error = new Error(message) as ApiError;
    error.name = 'ApiError';
    error.status = status;
    error.statusText = statusText;
    error.errorCode = errorCode;
    error.description = description;
    return error;
}

// ============================================================================
// BASE HTTP CLIENT
// ============================================================================

/**
 * Fetches CSRF token for write operations
 */
async function fetchCsrfToken(accessToken: string): Promise<string | null> {
    try {
        const response = await fetch(CSRF_URL, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            return null;
        }

        const data = (await response.json()) as { token?: string };
        return data?.token ?? null;
    } catch {
        return null;
    }
}

/**
 * Performs an authenticated HTTP request with CSRF and error handling
 */
async function authenticatedRequest<T>(
    config: RequestConfig,
    accessToken: string,
): Promise<T> {
    const { method, url, body, requiresCsrf = false } = config;

    // Determine if CSRF is needed
    const needsCsrf =
        requiresCsrf && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

    // Get CSRF token if needed
    let csrfToken: string | null = null;
    if (needsCsrf) {
        csrfToken = await fetchCsrfToken(accessToken);
    }

    // Build headers
    const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
    };

    if (csrfToken) {
        headers['X-XSRF-TOKEN'] = csrfToken;
    }

    // Build fetch config
    const fetchConfig: RequestInit = {
        method,
        headers,
        credentials: needsCsrf ? 'include' : undefined,
    };

    if (body) {
        fetchConfig.body = JSON.stringify(body);
    }

    // Build full URL
    const fullUrl = url.startsWith('http')
        ? url
        : `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;

    // Perform the request
    const response = await fetch(fullUrl, fetchConfig);

    // Handle errors
    if (!response.ok) {
        let errorData:
            | { errorCode?: string; description?: string; message?: string }
            | undefined;
        try {
            errorData = await response.json();
        } catch {
            // If the response is not JSON, ignore
        }

        // Handle 401 Unauthorized
        if (response.status === 401) {
            throw createApiError(
                'Unauthorized',
                401,
                'Unauthorized',
                'UNAUTHORIZED',
                'Token expired or invalid',
            );
        }

        // Create error with available information
        throw createApiError(
            getErrorMessage(errorData?.errorCode),
            response.status,
            response.statusText,
            errorData?.errorCode,
            errorData?.description ?? errorData?.message,
        );
    }

    // Parse the response
    const data = await response.json();

    // Handle wrapped response format (body + status)
    if (
        typeof data === 'object' &&
        data !== null &&
        'body' in data &&
        'status' in data
    ) {
        if (data.status >= 200 && data.status < 300) {
            return data.body as T;
        }
        // For non-success status in wrapped format, still return the body
        return data.body as T;
    }

    return data as T;
}

// ============================================================================
// ENDPOINT DEFINITIONS
// ============================================================================

/**
 * OrgEmployee endpoints
 */
export const orgEmployeeEndpoints = {
    /** Gets the authenticated org-employee data */
    get: (): RequestConfig => ({
        method: 'GET' as const,
        url: '/org-employee',
    }),
    /** Gets all employees in the organization */
    getEmployees: (): RequestConfig => ({
        method: 'GET' as const,
        url: '/employees',
    }),
};

/**
 * Employee Request endpoints
 */
export const employeeRequestEndpoints = {
    /** Gets a specific request for an employee */
    get: (employeeId: string, requestId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/employee/${employeeId}/request/${requestId}`,
    }),
    /** Gets all requests for an employee */
    getAll: (employeeId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/employee/${employeeId}/requests/all`,
    }),
    /** Gets open requests for an employee */
    getOpen: (employeeId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/employee/${employeeId}/requests/open`,
    }),
    /** Gets closed requests for an employee */
    getClosed: (employeeId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/employee/${employeeId}/requests/closed`,
    }),
    /** Updates an employee request */
    update: (
        employeeId: string,
        requestId: string,
        pattern: PutAvailabilityPattern,
    ): RequestConfig => ({
        method: 'PUT' as const,
        url: `/employee/${employeeId}/request/${requestId}`,
        body: pattern,
        requiresCsrf: true,
    }),
    /** Adds a comment to an employee request */
    addComment: (
        employeeId: string,
        requestId: string,
        comment: string,
    ): RequestConfig => ({
        method: 'PUT' as const,
        url: `/employee/${employeeId}/request/${requestId}/comment`,
        body: { value: comment } satisfies PutCommentRequest,
        requiresCsrf: true,
    }),
    /** Submits an employee request */
    submit: (employeeId: string, requestId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/employee/${employeeId}/request/${requestId}/submit`,
    }),
};

/**
 * OrgEmployee Request endpoints
 */
export const orgEmployeeRequestEndpoints = {
    /** Gets a specific request for an org-employee */
    get: (orgEmployeeId: string, requestId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/org-employee/${orgEmployeeId}/request/${requestId}`,
    }),
    /** Gets all requests for an org-employee */
    getAll: (orgEmployeeId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/org-employee/${orgEmployeeId}/requests/all`,
    }),
    /** Gets open requests for an org-employee */
    getOpen: (orgEmployeeId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/org-employee/${orgEmployeeId}/requests/open`,
    }),
    /** Gets closed requests for an org-employee */
    getClosed: (orgEmployeeId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/org-employee/${orgEmployeeId}/requests/closed`,
    }),
    /** Updates an org-employee request */
    update: (
        orgEmployeeId: string,
        requestId: string,
        pattern: PutAvailabilityPattern,
    ): RequestConfig => ({
        method: 'PUT' as const,
        url: `/org-employee/${orgEmployeeId}/request/${requestId}`,
        body: pattern,
        requiresCsrf: true,
    }),
    /** Adds a comment to an org-employee request */
    addComment: (
        orgEmployeeId: string,
        requestId: string,
        comment: string,
    ): RequestConfig => ({
        method: 'PUT' as const,
        url: `/org-employee/${orgEmployeeId}/request/${requestId}/comment`,
        body: { value: comment } satisfies PutCommentRequest,
        requiresCsrf: true,
    }),
    /** Submits an org-employee request */
    submit: (orgEmployeeId: string, requestId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/org-employee/${orgEmployeeId}/request/${requestId}/submit`,
    }),
};

/**
 * Message endpoints
 */
export const messageEndpoints = {
    /** Gets all messages for an employee */
    getAll: (employeeId: string): RequestConfig => ({
        method: 'GET' as const,
        url: `/employee/${employeeId}/messages`,
    }),
    /** Creates a message for an employee */
    create: (
        employeeId: string,
        dto: { message: string; priority: number },
    ): RequestConfig => ({
        method: 'POST' as const,
        url: `/employee/${employeeId}/message`,
        body: dto,
        requiresCsrf: true,
    }),
    /** Deletes a message from an employee */
    delete: (employeeId: string, messageId: string): RequestConfig => ({
        method: 'DELETE' as const,
        url: `/employee/${employeeId}/message/${messageId}`,
        requiresCsrf: true,
    }),
};

/**
 * Organization endpoints
 */
export const orgEndpoints = {
    /** Gets organization data */
    get: (): RequestConfig => ({ method: 'GET' as const, url: '/org' }),
};

// ============================================================================
// MAIN API SERVICE
// ============================================================================

/**
 * Main API service
 *
 * Provides methods to interact with all API endpoints in a centralized
 * and authenticated manner.
 *
 * @example
 * ```typescript
 * import { apiService } from '@shared/services/apiService';
 *
 * // Get org-employee
 * const orgEmployee = await apiService.orgEmployee.get(accessToken);
 *
 * // Get all requests for an org-employee
 * const requests = await apiService.requests.orgEmployee.getAll(orgEmployeeId, accessToken);
 * ```
 */
export const apiService = {
    /** OrgEmployee operations */
    orgEmployee: {
        /**
         * Gets the authenticated org-employee data
         */
        get: (accessToken: string): ApiResult<OrgEmployee> =>
            authenticatedRequest<OrgEmployee>(
                orgEmployeeEndpoints.get(),
                accessToken,
            ),

        /**
         * Gets all employees in the organization
         */
        getEmployees: (accessToken: string): ApiResult<RestList<Employee>> =>
            authenticatedRequest<RestList<Employee>>(
                orgEmployeeEndpoints.getEmployees(),
                accessToken,
            ),
    },

    /** Employee Request operations */
    requests: {
        employee: {
            /**
             * Gets a specific request for an employee
             */
            get: (
                employeeId: string,
                requestId: string,
                accessToken: string,
            ): ApiResult<EmployeeRequest> =>
                authenticatedRequest<EmployeeRequest>(
                    employeeRequestEndpoints.get(employeeId, requestId),
                    accessToken,
                ),

            /**
             * Gets all requests for an employee
             */
            getAll: (
                employeeId: string,
                accessToken: string,
            ): ApiResult<EmployeeRequestsList> =>
                authenticatedRequest<EmployeeRequestsList>(
                    employeeRequestEndpoints.getAll(employeeId),
                    accessToken,
                ),

            /**
             * Gets open requests for an employee
             */
            getOpen: (
                employeeId: string,
                accessToken: string,
            ): ApiResult<EmployeeRequestsList> =>
                authenticatedRequest<EmployeeRequestsList>(
                    employeeRequestEndpoints.getOpen(employeeId),
                    accessToken,
                ),

            /**
             * Gets closed requests for an employee
             */
            getClosed: (
                employeeId: string,
                accessToken: string,
            ): ApiResult<EmployeeRequestsList> =>
                authenticatedRequest<EmployeeRequestsList>(
                    employeeRequestEndpoints.getClosed(employeeId),
                    accessToken,
                ),

            /**
             * Updates an employee request
             */
            update: (
                employeeId: string,
                requestId: string,
                pattern: PutAvailabilityPattern,
                accessToken: string,
            ): ApiResult<EmployeeRequest> =>
                authenticatedRequest<EmployeeRequest>(
                    employeeRequestEndpoints.update(
                        employeeId,
                        requestId,
                        pattern,
                    ),
                    accessToken,
                ),

            /**
             * Adds a comment to an employee request
             */
            addComment: (
                employeeId: string,
                requestId: string,
                comment: string,
                accessToken: string,
            ): ApiResult<EmployeeRequest> =>
                authenticatedRequest<EmployeeRequest>(
                    employeeRequestEndpoints.addComment(
                        employeeId,
                        requestId,
                        comment,
                    ),
                    accessToken,
                ),

            /**
             * Submits an employee request
             */
            submit: (
                employeeId: string,
                requestId: string,
                accessToken: string,
            ): ApiResult<EmployeeRequest> =>
                authenticatedRequest<EmployeeRequest>(
                    employeeRequestEndpoints.submit(employeeId, requestId),
                    accessToken,
                ),
        },

        /** OrgEmployee Request operations */
        orgEmployee: {
            /**
             * Gets a specific request for an org-employee
             */
            get: (
                orgEmployeeId: string,
                requestId: string,
                accessToken: string,
            ): ApiResult<OrgEmployeeRequest> =>
                authenticatedRequest<OrgEmployeeRequest>(
                    orgEmployeeRequestEndpoints.get(orgEmployeeId, requestId),
                    accessToken,
                ),

            /**
             * Gets all requests for an org-employee
             */
            getAll: (
                orgEmployeeId: string,
                accessToken: string,
            ): ApiResult<OrgEmployeeRequestsList> =>
                authenticatedRequest<OrgEmployeeRequestsList>(
                    orgEmployeeRequestEndpoints.getAll(orgEmployeeId),
                    accessToken,
                ),

            /**
             * Gets open requests for an org-employee
             */
            getOpen: (
                orgEmployeeId: string,
                accessToken: string,
            ): ApiResult<OrgEmployeeRequestsList> =>
                authenticatedRequest<OrgEmployeeRequestsList>(
                    orgEmployeeRequestEndpoints.getOpen(orgEmployeeId),
                    accessToken,
                ),

            /**
             * Gets closed requests for an org-employee
             */
            getClosed: (
                orgEmployeeId: string,
                accessToken: string,
            ): ApiResult<OrgEmployeeRequestsList> =>
                authenticatedRequest<OrgEmployeeRequestsList>(
                    orgEmployeeRequestEndpoints.getClosed(orgEmployeeId),
                    accessToken,
                ),

            /**
             * Updates an org-employee request
             */
            update: (
                orgEmployeeId: string,
                requestId: string,
                pattern: PutAvailabilityPattern,
                accessToken: string,
            ): ApiResult<OrgEmployeeRequest> =>
                authenticatedRequest<OrgEmployeeRequest>(
                    orgEmployeeRequestEndpoints.update(
                        orgEmployeeId,
                        requestId,
                        pattern,
                    ),
                    accessToken,
                ),

            /**
             * Adds a comment to an org-employee request
             */
            addComment: (
                orgEmployeeId: string,
                requestId: string,
                comment: string,
                accessToken: string,
            ): ApiResult<OrgEmployeeRequest> =>
                authenticatedRequest<OrgEmployeeRequest>(
                    orgEmployeeRequestEndpoints.addComment(
                        orgEmployeeId,
                        requestId,
                        comment,
                    ),
                    accessToken,
                ),

            /**
             * Submits an org-employee request
             */
            submit: (
                orgEmployeeId: string,
                requestId: string,
                accessToken: string,
            ): ApiResult<OrgEmployeeRequest> =>
                authenticatedRequest<OrgEmployeeRequest>(
                    orgEmployeeRequestEndpoints.submit(
                        orgEmployeeId,
                        requestId,
                    ),
                    accessToken,
                ),
        },
    },

    /** Message operations */
    messages: {
        /**
         * Gets all messages for an employee
         */
        getAll: (
            employeeId: string,
            accessToken: string,
        ): ApiResult<RestList<EmployeeMessage>> =>
            authenticatedRequest<RestList<EmployeeMessage>>(
                messageEndpoints.getAll(employeeId),
                accessToken,
            ),

        /**
         * Creates a message for an employee
         */
        create: (
            employeeId: string,
            dto: { message: string; priority: number },
            accessToken: string,
        ): ApiResult<void> =>
            authenticatedRequest<void>(
                messageEndpoints.create(employeeId, dto),
                accessToken,
            ),

        /**
         * Deletes a message from an employee
         */
        delete: (
            employeeId: string,
            messageId: string,
            accessToken: string,
        ): ApiResult<void> =>
            authenticatedRequest<void>(
                messageEndpoints.delete(employeeId, messageId),
                accessToken,
            ),
    },

    /** Organization operations */
    org: {
        /**
         * Gets organization data
         */
        get: (accessToken: string): ApiResult<Org> =>
            authenticatedRequest<Org>(orgEndpoints.get(), accessToken),
    },
};

// ============================================================================
// EXPORTS
// ============================================================================

export {
    API_BASE_URL,
    CSRF_URL,
    fetchCsrfToken,
    authenticatedRequest,
    createApiError,
    getErrorMessage,
    ERROR_MESSAGES,
};
